import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  UserPlusIcon,
  PencilIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../../../config/api';
import OwnerDetailModal from './OwnerDetailModal';
import OwnerEditModal from './OwnerEditModal';

interface Owner {
  id: string;
  name: string;
  email: string;
  phone: string;
  businessName: string;
  businessNumber: string;
  facilities: number;
  status: 'active' | 'pending' | 'suspended';
  joinDate: string;
  lastLogin: string;
}

interface ContactApplication {
  id: string;
  name: string;
  company: string;
  phone: string;
  email: string;
  business_type: string;
  message: string;
  submitted_at: string;
  status: 'new' | 'approved' | 'rejected';
}

const OwnerManagement: React.FC = () => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [selectedApplication, setSelectedApplication] = useState<ContactApplication | null>(null);
  const [owners, setOwners] = useState<Owner[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // 업체 신청 관련 상태
  const [pendingApplications, setPendingApplications] = useState<ContactApplication[]>([]);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'owners' | 'applications'>('applications');
  
  // 모달 관련 상태
  const [selectedOwner, setSelectedOwner] = useState<Owner | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // 오너 목록 로드
  const loadOwners = async () => {
    try {
      const apiPath = API_BASE_URL.includes('/v1') ? '/admin' : '/api/v1/admin';
      const response = await fetch(`${API_BASE_URL}${apiPath}/owners`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken') || 'demo-token'}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // 백엔드 데이터를 프론트엔드 형식으로 변환
        const formattedOwners = data.owners.map((owner: any) => ({
          id: owner.id,
          name: owner.name,
          email: owner.email,
          phone: owner.phone || '',
          businessName: owner.organization_name || '',
          businessNumber: owner.business_number || '',
          facilities: 1, // 기본값
          status: owner.is_suspended ? 'suspended' : 'active',
          joinDate: owner.created_at ? new Date(owner.created_at).toLocaleDateString('ko-KR') : '',
          lastLogin: owner.last_login ? new Date(owner.last_login).toLocaleString('ko-KR') : '없음',
        }));
        setOwners(formattedOwners);
      } else {
        console.error('오너 목록 API 오류:', response.status, response.statusText);
        if (response.status === 401) {
          console.error('인증 토큰이 유효하지 않습니다. 다시 로그인하세요.');
        }
      }
    } catch (error) {
      console.error('오너 목록 로드 실패:', error);
    }
  };

  // 대기 중인 신청 목록 로드
  const loadPendingApplications = async () => {
    try {
      setLoading(true);
      const apiPath = API_BASE_URL.includes('/v1') ? '' : '/api/v1';
      const response = await fetch(`${API_BASE_URL}${apiPath}/contacts/?status=new&size=50`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken') || 'demo-token'}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const pending = data.contacts.filter((contact: ContactApplication) => contact.status === 'new');
        setPendingApplications(pending);
      } else {
        console.error('신청 목록 API 오류:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('신청 목록 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  // 오너 승인 처리
  const approveOwner = async (contactId: string) => {
    try {
      setProcessingId(contactId);
      
      // contactId를 문자열로 변환하고 'contact_'로 시작하는지 확인
      const contactIdStr = String(contactId);
      const fullContactId = contactIdStr.startsWith('contact_') ? contactIdStr : `contact_${contactIdStr}`;
      
      // API_BASE_URL이 /v1로 시작하면 /admin, 아니면 /api/v1/admin 사용
      const apiPath = API_BASE_URL.includes('/v1') ? '/admin' : '/api/v1/admin';
      const response = await fetch(`${API_BASE_URL}${apiPath}/approve-owner/${fullContactId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken') || 'demo-token'}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        alert(`승인이 완료되었습니다!\n\n오너 계정이 생성되었습니다.\n임시 비밀번호가 이메일로 발송되었습니다.\n\n임시 비밀번호: ${data.temporary_password}`);
        await loadPendingApplications();
        await loadOwners();
      } else {
        console.error('승인 API 오류:', response.status, response.statusText);
        const errorData = await response.text();
        console.error('오류 응답:', errorData);
        throw new Error(`승인 처리에 실패했습니다. (${response.status} ${response.statusText})`);
      }
    } catch (error) {
      console.error('승인 실패:', error);
      alert('승인 처리 중 오류가 발생했습니다.');
    } finally {
      setProcessingId(null);
    }
  };

  // 거부 확인 모달 열기
  const openRejectModal = (contactId: string) => {
    const application = pendingApplications.find(app => app.id === contactId);
    setSelectedContactId(contactId);
    setSelectedApplication(application || null);
    setShowRejectModal(true);
  };

  // 신청 거부 처리
  const rejectApplication = async () => {
    if (!selectedContactId) return;
    
    try {
      setProcessingId(selectedContactId);
      
      const apiPath = API_BASE_URL.includes('/v1') ? '' : '/api/v1';
      const response = await fetch(`${API_BASE_URL}${apiPath}/contacts/${selectedContactId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken') || 'demo-token'}`,
        },
        body: JSON.stringify({ status: 'rejected' }),
      });

      if (response.ok) {
        setShowRejectModal(false);
        setSelectedContactId(null);
        setSelectedApplication(null);
        alert('신청이 거부되었습니다.');
        await loadPendingApplications();
      } else {
        const errorText = await response.text();
        console.error('Error response:', response.status, errorText);
        throw new Error(`거부 처리에 실패했습니다: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('거부 실패:', error);
      alert('거부 처리 중 오류가 발생했습니다.');
    } finally {
      setProcessingId(null);
    }
  };

  // 사업 분야 한글 변환
  const getBusinessTypeLabel = (type: string) => {
    const types: { [key: string]: string } = {
      'studyroom': '독서실',
      'academy': '학원',
      'gym': '헬스장/피트니스',
      'smallbusiness': '소기업',
      'other': '기타'
    };
    return types[type] || type;
  };

  // 시간 포맷팅
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR') + ' ' + date.toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadPendingApplications();
    loadOwners();
  }, []);

  const filteredOwners = owners.filter((owner) => {
    const matchesSearch =
      owner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      owner.businessName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      owner.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === 'all' || owner.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: Owner['status']) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="w-4 h-4 mr-1" />
            활성
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <ClockIcon className="w-4 h-4 mr-1" />
            승인대기
          </span>
        );
      case 'suspended':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircleIcon className="w-4 h-4 mr-1" />
            정지
          </span>
        );
    }
  };

  const getStatusCount = (status: string) => {
    if (status === 'all') return owners.length;
    return owners.filter((owner) => owner.status === status).length;
  };

  // 모달 핸들러 함수들
  const handleViewOwner = (owner: Owner) => {
    setSelectedOwner(owner);
    setShowDetailModal(true);
  };

  const handleEditOwner = (owner: Owner) => {
    setSelectedOwner(owner);
    setShowDetailModal(false);
    setShowEditModal(true);
  };

  const handleSaveOwner = async (updatedOwner: Owner) => {
    try {
      // API 호출하여 오너 정보 업데이트
      const apiPath = API_BASE_URL.includes('/v1') ? '/admin' : '/api/v1/admin';
      const response = await fetch(`${API_BASE_URL}${apiPath}/owners/${updatedOwner.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify(updatedOwner),
      });

      if (response.ok) {
        alert('오너 정보가 수정되었습니다.');
        // 로컬 상태 업데이트
        setOwners(owners.map(o => o.id === updatedOwner.id ? updatedOwner : o));
        setShowEditModal(false);
      }
    } catch (error) {
      console.error('오너 정보 수정 실패:', error);
      alert('오너 정보 수정에 실패했습니다.');
    }
  };

  const handleResetPassword = async (ownerId: string) => {
    if (!window.confirm('비밀번호를 초기화하시겠습니까?')) return;

    try {
      const apiPath = API_BASE_URL.includes('/v1') ? '/admin' : '/api/v1/admin';
      const response = await fetch(`${API_BASE_URL}${apiPath}/owners/${ownerId}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        alert(`비밀번호가 초기화되었습니다.\n임시 비밀번호: ${data.temporary_password}`);
      }
    } catch (error) {
      console.error('비밀번호 초기화 실패:', error);
      alert('비밀번호 초기화에 실패했습니다.');
    }
  };

  const handleToggleSuspend = async (ownerId: string, suspend: boolean) => {
    try {
      const apiPath = API_BASE_URL.includes('/v1') ? '/admin' : '/api/v1/admin';
      const response = await fetch(`${API_BASE_URL}${apiPath}/owners/${ownerId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({
          is_suspended: suspend,
          suspended_reason: suspend ? '관리자에 의한 정지' : null,
        }),
      });

      if (response.ok) {
        alert(suspend ? '계정이 정지되었습니다.' : '계정이 활성화되었습니다.');
        // 로컬 상태 업데이트
        setOwners(owners.map(o => 
          o.id === ownerId 
            ? { ...o, status: suspend ? 'suspended' : 'active' } 
            : o
        ));
        setShowDetailModal(false);
      }
    } catch (error) {
      console.error('계정 상태 변경 실패:', error);
      alert('계정 상태 변경에 실패했습니다.');
    }
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">오너 관리</h1>
        <p className="mt-2 text-gray-600">
          사업자 등록 및 시설 운영자를 관리합니다.
        </p>
      </div>

      {/* 탭 네비게이션 */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('applications')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'applications'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              업체 신청 관리 ({pendingApplications.length})
            </button>
            <button
              onClick={() => setActiveTab('owners')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'owners'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              등록된 오너 ({owners.length})
            </button>
          </nav>
        </div>
      </div>

      {/* 업체 신청 관리 탭 */}
      {activeTab === 'applications' && (
        <div>
          {/* 통계 카드 */}
          <div className="mb-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <ClockIcon className="h-8 w-8 text-yellow-600" />
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">{pendingApplications.length}</h3>
                    <p className="text-sm text-gray-600">대기 중인 신청</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-8 w-8 text-green-600" />
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">{owners.length}</h3>
                    <p className="text-sm text-gray-600">승인된 오너</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <BuildingOfficeIcon className="h-8 w-8 text-blue-600" />
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">{owners.length}</h3>
                    <p className="text-sm text-gray-600">운영 중인 시설</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 대기 중인 신청 목록 */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                대기 중인 업체 신청
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                신규 업체 신청을 검토하고 승인/거부하세요.
              </p>
            </div>

            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <p className="text-gray-500">로딩 중...</p>
              </div>
            ) : pendingApplications.length === 0 ? (
              <div className="p-8 text-center">
                <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">대기 중인 신청이 없습니다.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {pendingApplications.map((application) => (
                  <div key={application.id} className="p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* 신청자 정보 */}
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">신청자 정보</h4>
                        <div className="space-y-3">
                          <div className="flex items-center">
                            <UsersIcon className="h-5 w-5 text-gray-400 mr-3" />
                            <span className="font-medium">{application.name}</span>
                          </div>
                          <div className="flex items-center">
                            <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-3" />
                            <span>{application.company}</span>
                          </div>
                          <div className="flex items-center">
                            <PhoneIcon className="h-5 w-5 text-gray-400 mr-3" />
                            <span>{application.phone}</span>
                          </div>
                          <div className="flex items-center">
                            <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3" />
                            <span>{application.email}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {getBusinessTypeLabel(application.business_type)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* 신청 내용 및 액션 */}
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">신청 내용</h4>
                        <div className="mb-4">
                          <p className="text-sm text-gray-600">
                            신청일: {formatDate(application.submitted_at)}
                          </p>
                        </div>
                        {application.message && (
                          <div className="mb-4">
                            <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">
                              {application.message}
                            </p>
                          </div>
                        )}

                        {/* 승인/거부 버튼 */}
                        <div className="flex space-x-3 mt-6">
                          <button
                            onClick={() => {
                              approveOwner(application.id);
                            }}
                            disabled={processingId === application.id}
                            className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {processingId === application.id ? (
                              <div className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                처리 중...
                              </div>
                            ) : (
                              <div className="flex items-center justify-center">
                                <CheckCircleIcon className="h-4 w-4 mr-2" />
                                승인
                              </div>
                            )}
                          </button>
                          <button
                            onClick={() => openRejectModal(application.id)}
                            disabled={processingId === application.id}
                            className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <div className="flex items-center justify-center">
                              <XCircleIcon className="h-4 w-4 mr-2" />
                              거부
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 등록된 오너 관리 탭 */}
      {activeTab === 'owners' && (
        <div>
          <div className="mb-6 bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-4">
            <div className="bg-blue-50 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <BuildingOfficeIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        전체 오너
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {getStatusCount('all')}명
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-green-50 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircleIcon className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        활성 오너
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {getStatusCount('active')}명
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-yellow-50 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ClockIcon className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        승인 대기
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {getStatusCount('pending')}명
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-red-50 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        정지된 오너
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {getStatusCount('suspended')}명
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              오너 목록
            </h3>
            <div className="mt-3 sm:mt-0 sm:ml-4 flex space-x-3">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="오너 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                className="block pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">전체 상태</option>
                <option value="active">활성</option>
                <option value="pending">승인대기</option>
                <option value="suspended">정지</option>
              </select>
              <button
                type="button"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <UserPlusIcon className="h-4 w-4 mr-2" />
                신규 등록
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  오너 정보
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  사업자 정보
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  시설 수
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  상태
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  가입일
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  최근 로그인
                </th>
                <th className="relative px-6 py-3">
                  <span className="sr-only">관리</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOwners.map((owner) => (
                <tr key={owner.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700">
                            {owner.name.charAt(0)}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {owner.name}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <EnvelopeIcon className="w-4 h-4 mr-1" />
                          {owner.email}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <PhoneIcon className="w-4 h-4 mr-1" />
                          {owner.phone}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {owner.businessName}
                    </div>
                    <div className="text-sm text-gray-500">
                      {owner.businessNumber}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {owner.facilities}개
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(owner.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {owner.joinDate}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {owner.lastLogin}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button 
                        onClick={() => handleViewOwner(owner)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleEditOwner(owner)}
                        className="text-green-600 hover:text-green-900"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
        </div>
      )}

      {/* 모달들 */}
      <OwnerDetailModal
        owner={selectedOwner}
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        onEdit={handleEditOwner}
        onResetPassword={handleResetPassword}
        onToggleSuspend={handleToggleSuspend}
      />

      <OwnerEditModal
        owner={selectedOwner}
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={handleSaveOwner}
      />

      {/* 거부 확인 모달 */}
      {showRejectModal && (
        <div className="fixed z-50 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              aria-hidden="true"
              onClick={() => setShowRejectModal(false)}
            ></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                    신청 거부 확인
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      정말로 이 신청을 거부하시겠습니까? 
                    </p>
                    {selectedApplication && (
                      <div className="mt-3 bg-gray-50 rounded-md p-3">
                        <p className="text-sm font-medium text-gray-900">{selectedApplication.name}</p>
                        <p className="text-sm text-gray-600">{selectedApplication.company}</p>
                        <p className="text-sm text-gray-600">{selectedApplication.email}</p>
                      </div>
                    )}
                    <p className="text-sm text-red-600 mt-3 font-medium">
                      거부된 신청은 복구할 수 없습니다.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={rejectApplication}
                  disabled={processingId === selectedContactId}
                >
                  {processingId === selectedContactId ? '처리 중...' : '거부'}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                  onClick={() => {
                    setShowRejectModal(false);
                    setSelectedContactId(null);
                    setSelectedApplication(null);
                  }}
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OwnerManagement;
